// src/stores/ui.ts
// UI store — global toast notifications, loading overlay, and dialog state.
// Use this instead of alert()/confirm() anywhere in the app.

import { defineStore } from 'pinia'
import { ref } from 'vue'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number   
}

export const useUIStore = defineStore('ui', () => {
  //  State
  const toasts = ref<Toast[]>([])
  const isPageLoading = ref(false)

  //  Toast actions
  function showToast(toast: Omit<Toast, 'id'>): void {
    const id = crypto.randomUUID()
    const duration = toast.duration ?? 4000

    toasts.value.push({ ...toast, id })

    if (duration > 0) {
      setTimeout(() => dismissToast(id), duration)
    }
  }

  function dismissToast(id: string): void {
    toasts.value = toasts.value.filter((t) => t.id !== id)
  }

  // Convenience helpers
  function success(title: string, message?: string): void {
    showToast({ type: 'success', title, message })
  }
  function error(title: string, message?: string): void {
    showToast({ type: 'error', title, message, duration: 6000 })
  }
  function warning(title: string, message?: string): void {
    showToast({ type: 'warning', title, message })
  }
  function info(title: string, message?: string): void {
    showToast({ type: 'info', title, message })
  }

  // Page loading
  function setPageLoading(val: boolean): void {
    isPageLoading.value = val
  }

  function reset(): void {
    toasts.value = []
    isPageLoading.value = false
  }

  return {
    toasts,
    isPageLoading,
    showToast,
    dismissToast,
    success,
    error,
    warning,
    info,
    setPageLoading,
    reset,
  }
})
